import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ArrowRight, Clock, Calendar, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { APPLY_URL } from '../mock';

export const ApplicationForm3D = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [mousePosition, setMousePosition] = React.useState({ x: 0, y: 0 });
  const cardRef = React.useRef(null);

  const handleMouseMove = (e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 10;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * -10;
    setMousePosition({ x, y });
  };

  const steps = [
    {
      icon: Clock,
      title: '60 Seconds',
      description: 'Quick application to assess fit'
    },
    {
      icon: Calendar,
      title: '15-Min Call',
      description: 'Strategy call if it\'s a match'
    },
    {
      icon: MessageSquare,
      title: 'Clear Plan',
      description: 'Exact roadmap for your 12, 24, or 52-week program'
    }
  ];

  return (
    <section id="apply" ref={ref} className="relative bg-panel py-24 md:py-32 px-6 overflow-hidden">
      {/* Animated Background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-1/2 -left-1/2 w-full h-full border border-[#c6a04a]/5 rounded-full"
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360]
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
        />
        <motion.div
          className="absolute -bottom-1/2 -right-1/2 w-full h-full border border-[#d9be7e]/5 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            rotate: [360, 180, 0]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        />
      </div>

      <div className="max-w-5xl mx-auto relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16 space-y-6"
          initial={{ opacity: 0, y: -100 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <motion.p 
            className="font-accent text-xl md:text-2xl italic text-gold"
            initial={{ opacity: 0, scale: 0 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Ready to Start?
          </motion.p>
          <motion.h2 
            className="font-hero text-3xl md:text-5xl lg:text-6xl uppercase tracking-wide text-cream"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            style={{ fontWeight: 700 }}
          >
            Ready to Stop
            <br />
            <span className="text-gold">Spinning Your Wheels?</span>
          </motion.h2>
          <motion.p 
            className="font-body text-base md:text-lg text-[#e8e6df] max-w-2xl mx-auto leading-relaxed font-light"
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            Apply below. If it's a fit, we'll schedule a 15-minute strategy call to map out your exact plan.
            <br />
            No pressure. No fluff. Just clarity on whether CT3 is right for you.
          </motion.p>
        </motion.div>

        {/* 3D Steps */}
        <motion.div
          className="grid md:grid-cols-3 gap-6 mb-12"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={{
            visible: {
              transition: { staggerChildren: 0.15, delayChildren: 0.5 }
            }
          }}
        >
          {steps.map((step, index) => (
            <motion.div
              key={index}
              variants={{
                hidden: { opacity: 0, y: 50, rotateY: -45 },
                visible: { opacity: 1, y: 0, rotateY: 0 }
              }}
              transition={{ duration: 0.6, type: "spring" }}
              style={{ perspective: '1000px' }}
            >
              <motion.div
                className="bg-dark border border-gold/30 p-6 text-center h-full group cursor-pointer relative overflow-hidden"
                whileHover={{
                  borderColor: 'rgba(198, 160, 74, 0.8)',
                  boxShadow: '0 20px 40px rgba(198, 160, 74, 0.2)',
                  scale: 1.03,
                  rotateY: 5
                }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                style={{ transformStyle: 'preserve-3d' }}
              >
                <motion.div
                  className="w-14 h-14 mx-auto mb-4 relative flex items-center justify-center"
                  style={{ transform: 'translateZ(30px)' }}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                >
                  <div className="absolute inset-0 border-2 border-gold/40 transform rotate-45 group-hover:border-gold transition-colors duration-300" />
                  <step.icon className="w-6 h-6 text-gold relative z-10" />
                </motion.div>

                <div style={{ transform: 'translateZ(20px)' }}>
                  <p className="font-display text-xl uppercase tracking-wide text-cream mb-2 group-hover:text-gold transition-colors duration-300">
                    {step.title}
                  </p>
                  <p className="font-body text-sm text-[#e8e6df]/80 font-light">
                    {step.description}
                  </p>
                </div>

                {/* Hover glow */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: 'radial-gradient(circle at 50% 50%, rgba(198, 160, 74, 0.15), transparent 70%)'
                  }}
                />
              </motion.div>
            </motion.div>
          ))}
        </motion.div>

        {/* Main CTA Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.8, rotateX: -45 }}
          animate={inView ? { opacity: 1, scale: 1, rotateX: 0 } : {}}
          transition={{ delay: 0.8, duration: 1, type: "spring" }}
          style={{ perspective: '2000px' }}
        >
          <motion.div
            ref={cardRef}
            className="bg-dark p-10 md:p-16 border-2 border-gold/40 text-center relative overflow-hidden"
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setMousePosition({ x: 0, y: 0 })}
            animate={{
              rotateX: mousePosition.y * 0.5,
              rotateY: mousePosition.x * 0.5,
            }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{
              transformStyle: 'preserve-3d',
              boxShadow: '0 30px 80px rgba(198, 160, 74, 0.3)'
            }}
          >
            {/* Shine */}
            <div 
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{
                background: `radial-gradient(circle at ${mousePosition.x * 5 + 50}% ${mousePosition.y * 5 + 50}%, rgba(198, 160, 74, 0.2), transparent 50%)`,
              }}
            />

            <div style={{ transform: 'translateZ(40px)' }}>
              <p className="font-accent text-lg md:text-xl italic text-gold-soft mb-6">
                Limited spots — by application only
              </p>
              
              <motion.div
                whileHover={{ scale: 1.05, rotateX: 5 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
                style={{ perspective: '1000px' }}
              >
                <Button
                  onClick={() => window.open(APPLY_URL, '_blank', 'noopener,noreferrer')}
                  className="bg-[#c6a04a] hover:bg-[#d9be7e] text-[#0d0d0d] font-semibold text-sm md:text-xl px-6 md:px-12 py-5 md:py-7 rounded-none uppercase tracking-wider transition-all duration-300 shadow-2xl shadow-[#c6a04a]/50 whitespace-normal h-auto"
                >
                  Apply for Your<br className="md:hidden" /> Strategy Call
                  <ArrowRight className="ml-2 md:ml-3 h-4 w-4 md:h-5 md:w-5" />
                </Button>
              </motion.div>
              
              <p className="font-body text-sm text-gold-soft mt-6">
                Takes 60 seconds to apply
              </p>
              <p className="font-body text-xs text-[#e8e6df]/60 mt-2">
                Only a limited number of Strategy Calls are scheduled each week
              </p>
            </div>

            {/* Corner Accents */}
            <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-gold" />
            <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-gold" />
            <div className="absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-gold" />
            <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-gold" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
